var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import { getOwner } from '@ember/application';
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import * as Sentry from '@sentry/ember';
import * as singleSpa from 'single-spa';
import 'systemjs';
import { getDatadogRum } from '@square/dashboard-utilities/utils/datadog-rum';
const LOCALHOST_DEV_PORT = '5173';
const API_PATH = '/staff-fe/api/dashboard-spa-manifest';
let StaffFeService = (_class = class StaffFeService extends Service {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "environment", _descriptor, this);
    _initializerDefineProperty(this, "employeeInfo", _descriptor2, this);
    _initializerDefineProperty(this, "userInfo", _descriptor3, this);
    _initializerDefineProperty(this, "applicationRouter", _descriptor4, this);
    _initializerDefineProperty(this, "queryClient", _descriptor5, this);
    _initializerDefineProperty(this, "navigationShim", _descriptor6, this);
    _defineProperty(this, "modules", {});
    _defineProperty(this, "cdnOrigin", void 0);
    _defineProperty(this, "bundles", {});
    _defineProperty(this, "parcelConfig", {
      bootstrap: () => Promise.resolve(),
      mount: () => Promise.resolve(),
      unmount: () => Promise.resolve(),
      update: () => Promise.resolve()
    });
    _initializerDefineProperty(this, "mountFailure", _descriptor7, this);
    _initializerDefineProperty(this, "apiEndpointFailure", _descriptor8, this);
  }
  async setupApp(bundleName, domElement, customProps, reactType) {
    const mfeBundleUrl = await this.loadManifestData(bundleName);
    let parcel;
    if (mfeBundleUrl) {
      try {
        this.navigationShim.start();
        this.modules[bundleName] = await System.import(mfeBundleUrl);
        if (this.modules[bundleName]) {
          parcel = await this.mountApplication(this.modules[bundleName], domElement, customProps, reactType);
        }
      } catch (e) {
        this.mountFailure = true;
        Sentry.captureException(e);
      }
    } else {
      this.apiEndpointFailure = true;
    }
    return parcel;
  }
  async preloadApp(bundleName) {
    // If the module is already loaded we don't need to preload it again
    if (this.modules[bundleName]) {
      return this.modules[bundleName];
    }
    const emptyDOMElement = document.createElement('div');
    const mfeBundleUrl = await this.loadManifestData(bundleName);
    if (mfeBundleUrl) {
      try {
        this.navigationShim.start();
        this.modules[bundleName] = await System.import(mfeBundleUrl);
        if (this.modules[bundleName]) {
          await this.mountApplication(this.modules[bundleName], emptyDOMElement, {
            preload: true,
            ...this.customProps
          });
        }
      } catch (e) {
        Sentry.captureException(e);
      }
    }
    return this.modules[bundleName];
  }
  async mountApplication(module, domElement, customProps, reactType) {
    this.parcelConfig = {
      bootstrap: module.bootstrap,
      mount: module.mount,
      unmount: module.unmount,
      update: module.update
    };
    const parcelProps = {
      domElement,
      ...customProps
    };
    const parcel = singleSpa.mountRootParcel(this.parcelConfig, parcelProps);
    await parcel.mountPromise;
    getDatadogRum().setViewContextProperty('reactType', reactType || 'unknown');
    return parcel;
  }
  async loadManifestData(bundle) {
    if (this.cdnOrigin && this.bundles[bundle]) {
      return `${this.cdnOrigin}/${this.bundles[bundle]}`;
    }
    let responseJson;
    try {
      const response = await fetch(this.endpoint);
      if (response.ok) {
        responseJson = await response.json();
      } else {
        throw new Error(`Bad response code ${response.status} from staff-fe API endpoint`);
      }
    } catch (e) {
      Sentry.captureException(e);
    }
    const {
      cdnOrigin,
      ...bundles
    } = responseJson?.app ?? {};
    this.cdnOrigin = cdnOrigin;
    this.bundles = bundles;
    return `${cdnOrigin}/${bundles[bundle]}`;
  }
  get config() {
    return getOwner(this).resolveRegistration('config:environment');
  }
  get endpoint() {
    const {
      isProduction,
      isSandbox,
      isTesting,
      isDevelopment
    } = this.environment;
    if (this.useLocalIntegration && isDevelopment) {
      return `http://localhost:${LOCALHOST_DEV_PORT}${API_PATH}`;
    } else if (isTesting) {
      return API_PATH;
    } else if (isProduction || isSandbox) {
      return `https://app.squareup.com${API_PATH}`;
    } else {
      return `https://app.squareupstaging.com${API_PATH}`;
    }
  }

  // In development, most users will not be running the integration
  // server for a micro frontend, so the default should be to use
  // its staging URL.
  // When a developer has the integration server running, they can
  // manually change useLocalIntegration to `true`.
  // For a developer that often wants integration mode, you can set the
  // INTEGRATION_STAFF_FE env variable to 'true'. Note that
  // the env variable must be set before starting the dashboard dev server.
  get useLocalIntegration() {
    return this.config.dashboardMFE?.['staff-fe']?.enabled;
  }
  get customProps() {
    const {
      merchantToken,
      employmentToken,
      countryCode,
      languageCode,
      isJP,
      browserLanguage,
      currencyCode,
      merchantProfile
    } = this.userInfo;
    const {
      canManageScheduleRequests,
      canViewWagesInShifts,
      canManageSchedules,
      isOwner,
      isAccountOwner
    } = this.employeeInfo;
    const {
      instance: queryClientInstance
    } = this.queryClient;
    return {
      userInfo: {
        merchantToken,
        employmentToken,
        countryCode,
        languageCode,
        isJP,
        browserLanguage,
        currencyCode,
        merchantProfile
      },
      employeePermissions: {
        canManageScheduleRequests,
        canViewWagesInShifts,
        canManageSchedules,
        isOwner,
        isAccountOwner
      },
      externalRoutes: {
        createTimecard: 'shifts.labor-cost.new',
        editTimecard: 'shifts.labor-cost.employee.shift',
        createTeamMember: 'team.team-members.consolidated-new',
        editTeamMember: 'team.team-members.edit',
        permissionSet: 'permission-set',
        scheduling: 'shifts.schedule.shifts',
        timecards: 'shifts.labor-cost',
        announcements: 'team-communication.announcements',
        teamSales: 'reports.staff_team_sales',
        laborVsSales: 'reports.staff_labor',
        commissions: 'team.commissions',
        onboarding: 'team.onboarding'
      },
      queryClient: queryClientInstance,
      applicationRouter: this.applicationRouter
    };
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "environment", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "employeeInfo", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "userInfo", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "applicationRouter", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "queryClient", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "navigationShim", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "mountFailure", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "apiEndpointFailure", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _class);
export { StaffFeService as default };